import { filter } from 'rxjs/operators';

import { Injectable } from '@angular/core';
import { Event, NavigationEnd, NavigationStart, Router } from '@angular/router';

import { genericizeUrlHash } from '@lib/url';

export enum NAV_PERFORMANCE_MARKERS {
  NAV_START_MARKER = 'navigation-start-maker',
  NAV_END_MARKER = 'navigation-end-maker',
}

export enum ENTRY_TYPE {
  RESOURCE = 'resource',
}

export const OBSERVER_TIMEOUT: number = 20_000;

export const routeChangeLoadingTimesStore: Map<string, number> = new Map<string, number>();

@Injectable({
  providedIn: 'root',
})
export class PerformanceMonitoringNavigationService {
  public currentPagePath: string;
  public timer: number;

  constructor(private router: Router) {
  }

  public monitorNavigation(): void {
    this.router.events
      .pipe(filter(event => event instanceof NavigationStart || event instanceof NavigationEnd))
      .subscribe((event: Event) => {
        if (event instanceof NavigationStart) {
          this.onNavigationStart(event);
        }

        if (event instanceof NavigationEnd) {
          this.onNavigationEnd(event);
        }
      });
  }

  public onNavigationStart(event: NavigationStart): void {
    const existingMarker: PerformanceEntryList = performance.getEntriesByName(NAV_PERFORMANCE_MARKERS.NAV_START_MARKER, 'mark');
    if (existingMarker?.length) performance.clearMarks(NAV_PERFORMANCE_MARKERS.NAV_START_MARKER);

    window.performance.mark(NAV_PERFORMANCE_MARKERS.NAV_START_MARKER);
  }

  public onNavigationEnd(event: NavigationEnd): void {
    // nav end event
  }

  public takeMeasurement(pagePath?: string): void {
    const currPagePath: string = pagePath || getUrlHashGroup(window.location.href.toString());
    window.performance.mark(NAV_PERFORMANCE_MARKERS.NAV_END_MARKER);
    this.handleRouteChangeLoadingTime(null, currPagePath);
  }

  private takeMeasurementForWatchAPI(endMarkerTime: number | null = null, pagePath?: string): void {
    const currPagePath: string = pagePath || getUrlHashGroup(window.location.href.toString());
    this.handleRouteChangeLoadingTime(endMarkerTime, currPagePath);
  }

  private handleRouteChangeLoadingTime(endMarkerTime: number | null = null, currPagePath: string): void {
    const routeChangeLoadingTime: number | null = getRouteChangeLoadingTime(currPagePath, endMarkerTime);
    if (routeChangeLoadingTime !== null) {
      logRouteChangeLoadingTimeInStore(currPagePath, routeChangeLoadingTime);
    }
    clearMarkers();
  }

  private observeAPIs(
    apiUrls: string[],
    callback: (lastApiResponseTime: number, lastApiUrl: string) => void,
    errorCallback?: (error: string) => void,
  ): void {
    const regexURLs: RegExp[] = apiUrls.map(convertToDatadogURLs);
    const unresolvedApis: Set<string> = new Set(apiUrls);
    let lastApiResponseTime: number = 0;
    let lastApiUrl: string = '';

    const observer: PerformanceObserver = new PerformanceObserver((list: PerformanceObserverEntryList): void => {
      const entries: PerformanceResourceTiming[] = list.getEntriesByType(ENTRY_TYPE.RESOURCE) as PerformanceResourceTiming[];

      entries.forEach((entry: PerformanceResourceTiming): void => {
        regexURLs.forEach((regex: RegExp, idx: number): void => {
          if (regex.test(entry.name) && entry.entryType === ENTRY_TYPE.RESOURCE) {
            if (entry.responseEnd > lastApiResponseTime) {
              lastApiResponseTime = entry.responseEnd;
              lastApiUrl = apiUrls[idx];
            }

            unresolvedApis.delete(apiUrls[idx]);
            if (unresolvedApis.size === 0) {
              observer.disconnect();
              unresolvedApis.clear();
              callback(lastApiResponseTime, lastApiUrl);
            }
          }
        });
      });
    });

    observer.observe({ type: ENTRY_TYPE.RESOURCE });

    setTimeout(() => {
      if (unresolvedApis.size > 0) {
        observer.disconnect();
        const errorMsg: string = 'Some APIs did not resolve within the timeout: ' + Array.from(unresolvedApis).join(', ');
        unresolvedApis.clear();
        console.error(errorMsg);
        if (errorCallback) {
          errorCallback(errorMsg);
        }
      }
    }, OBSERVER_TIMEOUT);
  }

  public watchAPIsAndCallback(
    apiUrls: string[],
    callback: (...args: any[]) => void,
    pagePath?: string,
  ): void {
    if (!Array.isArray(apiUrls) || apiUrls.length === 0) {
      console.error('Invalid API array provided.');
      return;
    }

    this.observeAPIs(
      apiUrls,
      (lastApiResponseTime: number, lastApiUrl: string) => {
        callback();
      },
    );
  }


  public watchAPIAndTakeMeasurement(apiUrls: string[], pagePath?: string): void {
    if (!Array.isArray(apiUrls) || apiUrls.length === 0) {
      console.error('Invalid API array provided.');
      return;
    }
    const currPagePath: string = pagePath || getUrlHashGroup(window.location.href.toString());
    this.observeAPIs(apiUrls, (lastApiResponseTime, lastApiUrl) => {
      this.takeMeasurementForWatchAPI(lastApiResponseTime, currPagePath);
    });
  }

}

// Utility functions
function logRouteChangeLoadingTimeInStore(pagePath: string, routeChangeLoadingTime: number): void {
  routeChangeLoadingTimesStore.set(pagePath, routeChangeLoadingTime);
}

function clearMarkers(): void {
  window.performance.clearMarks(NAV_PERFORMANCE_MARKERS.NAV_START_MARKER);
  window.performance.clearMarks(NAV_PERFORMANCE_MARKERS.NAV_END_MARKER);
}

function getRouteChangeLoadingTime(measureName?: string, endTime: number | null = null): number | null {
  try {
    const startMarkers: PerformanceEntryList =
      window.performance.getEntriesByName(NAV_PERFORMANCE_MARKERS.NAV_START_MARKER, 'mark');
    if (!startMarkers?.length) {
      return null;
    }

    let measure;
    if (endTime === null) {
      measure = window.performance.measure(
        measureName,
        NAV_PERFORMANCE_MARKERS.NAV_START_MARKER,
        NAV_PERFORMANCE_MARKERS.NAV_END_MARKER,
      );
    } else {
      /**
       * Manually calculate the duration when endTime is provided.
       * This is necessary because we cannot mark the performance timing i.e NAV_PERFORMANCE_MARKERS.NAV_END_MARKER
       * in the past. Instead, we compute the duration using the provided
       * endTime and the start time of the NAV_START_MARKER.
       */
      const startTime: number = startMarkers.slice(-1)[0].startTime;
      measure = {
        duration: endTime - startTime,
      };
    }
    return measure?.['duration'] || null;
  } catch (error) {
    console.error(error);
    return null;
  }
}

export function getUrlHashGroup(hash: string): string {
  return hash.indexOf('#') !== -1 ? genericizeUrlHash(hash.substring(hash.indexOf('#') + 1)) : genericizeUrlHash(hash);
}

function getCurrentPagePath(router: Router): string {
  return router?.url?.split(';')[0];
}

export function convertToDatadogURLs(pattern: string): RegExp {
  const regexString: string = pattern
    .replace(/\[\?\]/g, '.*')
    .replace(/\?/g, '\\?')
    .replace(/&/g, '\\&')
    .replace(/=/g, '\\=')
    .replace(/\//g, '\\/');

  return new RegExp(regexString);
}

/**
 * Page routes contains / forward slashes, which can't be used to create a metric on datadog.
 * Hence we need to replace any forward slash (in between the routes), and remove from starting and end if any.
 * [e.g] /safety/events/ => safety-events
 */
export function createPageMeasureNameFromRoute(pageRoute: string): string {
  // Remove leading and trailing slashes
  const trimmed: string = pageRoute.replace(/^\/+|\/+$/g, '');
  // Replace middle slashes with hyphens
  return trimmed.replace(/\//g, '-');
}
